import { FC, useEffect, useRef, useState } from "react";
import { Controller } from "react-hook-form";
import Select from "react-select";
import AsyncSelect from "react-select/async";
import { createLoadOptions } from "../../hooks/useDebounce";
import { useI18n } from "../../hooks/useTranslations";
import { CarLeasing } from "../../http/carLeasings/types";
import { useAdminCarsApi } from "../../http/cars/api";
import { CarNoCarDepency } from "../../http/cars/types";
import { useLeasingCompaniesApi } from "../../http/leasingCompanies/api";
import { LeasingCompany } from "../../http/leasingCompanies/types";
import { PaginationQueryWithFields } from "../../types";
import DatePicker from "../../ui/DatePicker";
import Form from "../ui/Form";
import FormField from "../ui/FormField";
import FormFieldsContainer from "../ui/FormFieldsContainer";
import Input from "../ui/Input";
import reactSelectStyles from "../ui/ReactSelectUtils";

const CAR_FIELDS = ["id", "vehicleIdentificationNumber", "currentPlateNumber", "lastPlateNumber"] as const;
type CarField = (typeof CAR_FIELDS)[number];
type CarSearch = Pick<CarNoCarDepency, CarField>;

const LEASING_COMPANY_FIELDS = ["id", "name"] as const;
type LeasingCompanyFiels = (typeof LEASING_COMPANY_FIELDS)[number];
type LeasingCompanySearch = Pick<LeasingCompany, LeasingCompanyFiels>;

export interface CarLeasingFormBody {
  car: {
    id: string;
    vehicleIdentificationNumber: string;
    currentPlateNumber?: string;
    lastPlateNumber?: string;
  } | null;
  company: LeasingCompanySearch | null;
  details: string | null;
  contractDate: string;
  expirationDate: string;
}

type CarLeasingFormProps = {
  carLeasing?: CarLeasing | null;
  onSubmit: (form: CarLeasingFormBody) => Promise<void>;
  isDisabled?: boolean;
  isLoading?: boolean;
  carId?: string;
};

const CarLeasingForm: FC<CarLeasingFormProps> = ({
  carLeasing,
  onSubmit,
  isDisabled,
  isLoading,
  carId,
}: CarLeasingFormProps) => {
  const i18n = useI18n();
  const carsApi = useAdminCarsApi();
  const leasingCompaniesApi = useLeasingCompaniesApi();
  const aborter = useRef(new AbortController());

  const [defaultCarOptions, setDefaultCarOptions] = useState<CarSearch[]>([]);

  const leasingCompanies = leasingCompaniesApi.useAdminLeasingCompanies(
    { limit: 100, fields: LEASING_COMPANY_FIELDS },
    aborter.current.signal,
  );

  // const carIds = leasingData.map((x) => x.car.id);
  // const selectedCars = cars.data?.items.filter((x) => !carIds.includes(x.id));

  const defaultValues = {
    car: carLeasing?.car,
    company: carLeasing?.leasingCompany,
    details: carLeasing?.contractDetails,
    contractDate: carLeasing?.contractDate,
    expirationDate: carLeasing?.expirationDate,
  };

  return (
    <Form<CarLeasingFormBody>
      defaultValues={defaultValues}
      submitText={i18n.t.common.save}
      disabled={isDisabled}
      onSubmit={() => onSubmit}
      itemCreationInfo={carLeasing}
    >
      {({ register, formState: { errors }, control, reset, watch, setValue }) => {
        useEffect(() => {
          reset(defaultValues);
        }, [carLeasing]);

        return (
          <FormFieldsContainer>
            {!carId && (
              <Controller
                name="car"
                control={control}
                rules={{ required: true }}
                render={({ field, fieldState }) => (
                  <FormField label={i18n.t.cars.singular} error={fieldState.error}>
                    {({ labelId, isInvalid }) => {
                      const searchTerm = useRef("");
                      const aborter = useRef(new AbortController());

                      const query: PaginationQueryWithFields<CarSearch> = {
                        filters: [{ fieldname: "c.purchase_type", values: ["purchase"] }],
                        fields: CAR_FIELDS,
                      };

                      const loadOptions = createLoadOptions<CarSearch, CarField>(
                        aborter,
                        searchTerm,
                        carsApi.getCars,
                        query,
                      );

                      useEffect(() => {
                        loadOptions(searchTerm.current).then(setDefaultCarOptions);
                      }, []);

                      return (
                        <AsyncSelect
                          menuPosition="fixed"
                          {...field}
                          inputId={labelId}
                          defaultOptions={defaultCarOptions}
                          loadOptions={loadOptions}
                          isClearable
                          isLoading={isLoading}
                          isDisabled={!!carLeasing?.car || isDisabled || isLoading}
                          placeholder={`${i18n.t.common.select}...`}
                          getOptionLabel={(x) =>
                            x.currentPlateNumber || x.lastPlateNumber || x.vehicleIdentificationNumber
                          }
                          getOptionValue={(x) => x.id}
                          styles={reactSelectStyles<{
                            id: string;
                            vehicleIdentificationNumber: string;
                            currentPlateNumber?: string;
                            lastPlateNumber?: string;
                          }>(isInvalid)}
                        />
                      );
                    }}
                  </FormField>
                )}
              />
            )}
            <Controller
              name="company"
              control={control}
              rules={{ required: true }}
              render={({ field, fieldState }) => (
                <FormField label={i18n.t.leasingCompanies.singular} error={fieldState.error}>
                  {({ labelId, isInvalid }) => (
                    <Select
                      menuPosition="fixed"
                      {...field}
                      inputId={labelId}
                      isDisabled={isDisabled || isLoading}
                      isLoading={isLoading}
                      options={leasingCompanies.data?.items}
                      isClearable
                      placeholder={`${i18n.t.common.select}...`}
                      getOptionLabel={(x) => x.name}
                      getOptionValue={(x) => x.id}
                      styles={reactSelectStyles<LeasingCompanySearch>(isInvalid)}
                    />
                  )}
                </FormField>
              )}
            />
            <Controller
              name="contractDate"
              control={control}
              rules={{
                required: true,
                validate: (value) => {
                  const d = new Date(value!);
                  if (d.getFullYear() < 1000) {
                    return i18n.t.common.invalidDate;
                  }
                  return undefined;
                },
              }}
              render={({ fieldState }) => (
                <FormField label={i18n.t.common.contractDate} error={fieldState.error}>
                  {({ isInvalid }) => (
                    <DatePicker
                      onChange={(v) => !!v && setValue("contractDate", v)}
                      value={watch("contractDate")}
                      disabled={isDisabled || isLoading}
                      isInvalid={isInvalid}
                    />
                  )}
                </FormField>
              )}
            />
            <Controller
              name="expirationDate"
              control={control}
              rules={{
                required: true,
                validate: (value) => {
                  if (value) {
                    const d = new Date(value);
                    if (d.getFullYear() < 1000) {
                      return i18n.t.common.invalidDate;
                    }
                    const contractDate = watch("contractDate");
                    if (contractDate) {
                      if (value == contractDate) {
                        return i18n.t.carLeasings.errors.expirationDateCantMatchContractDate;
                      }
                      if (value < contractDate) {
                        return i18n.t.carLeasings.errors.expirationDateCantBeBeforeContractDate;
                      }
                    }
                  }
                  return undefined;
                },
              }}
              render={({ fieldState }) => (
                <FormField label={i18n.t.common.expirationDate} error={fieldState.error}>
                  {({ isInvalid }) => (
                    <DatePicker
                      onChange={(v) => !!v && setValue("expirationDate", v)}
                      value={watch("expirationDate")}
                      disabled={isDisabled || isLoading}
                      isInvalid={isInvalid}
                    />
                  )}
                </FormField>
              )}
            />
            <FormField label={i18n.t.common.contractDetails} error={errors.details} isOptional>
              {({ labelId }) => <Input id={labelId} disabled={isDisabled || isLoading} {...register("details")} />}
            </FormField>
          </FormFieldsContainer>
        );
      }}
    </Form>
  );
};

export default CarLeasingForm;
